import { camelToSnake, snakeToCamel } from "caseparser";
import { t } from "i18next";

import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";

export const usersApi = api
  .enhanceEndpoints({ addTagTypes: ["user", "profiles", "users"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      users: builder.query({
        query: ({ search, branchId }: any) => ({
          url: `/users`,
          method: RequestMethod.GET,
          params: camelToSnake({
            search,
            branchId,
          }),
        }),
        providesTags: ["users"],
        transformResponse: (response: any) => snakeToCamel(response.data),
      }),
      fetchUsers: builder.mutation({
        query: (params) => ({
          url: "admin/users",
          method: RequestMethod.GET,
          params,
        }),
      }),
      storeUser: builder.mutation({
        query: (gymData) => ({
          url: "admin/users/store",
          method: RequestMethod.POST,
          body: gymData,
        }),
      }),
      user: builder.query({
        query: ({ id }: any) => ({
          url: `/users/${id}`,
          method: RequestMethod.GET,
        }),
        providesTags: ["user"],
        transformResponse: (response: any) => snakeToCamel(response.data),
      }),
      updateUser: builder.mutation({
        invalidatesTags: ["user"],
        query: ({ id, active, name, email }: any) => ({
          url: `/users/${id}`,
          method: RequestMethod.PUT,
          body: snakeToCamel({
            active,
            name,
            email,
          }),
        }),
      }),
      deleteUser: builder.mutation({
        query: ({ id }) => ({
          url: `/users/${id}`,
          method: RequestMethod.DELETE,
        }),
      }),
      getAdvance: builder.query({
        query: ({ id }) => ({
          url: `/getUserAdvance/${id}`,
          method: RequestMethod.GET,
        }),
      }),
      exportUsers: builder.query({
        query({
          branchId,
          search,
          page,
          limit,
          gym,
          status,
          sex,
          country,
          initialAge,
          finalAge,
          payFee,
          payCourse,
          multiCenter,
          cuotas,
          feesCourses,
          courses,
          bonds,
          spaceBonuses,
          lest,
          trainingCreatedBy,
          nutritionCreatedBy,
          userTags,
          marketingTags,
        }) {
          return {
            url: `admin/users/export`,
            method: RequestMethod.GET,
            responseHandler: async (response) => {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${t("user.views.users")}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            },
            params: camelToSnake({
              branchId,
              search,
              page,
              limit,
              gym,
              status,
              sex,
              country,
              initialAge,
              finalAge,
              payFee,
              payCourse,
              multiCenter,
              cuotas,
              feesCourses,
              courses,
              bonds,
              spaceBonuses,
              lest,
              trainingCreatedBy,
              nutritionCreatedBy,
              userTags,
              marketingTags,
            }),
            cache: "no-cache",
          };
        },
      }),
      stateUser: builder.mutation({
        invalidatesTags: ["user"],
        query: ({ id, state }: any) => ({
          url: `admin/users/state/${id}`,
          method: RequestMethod.PUT,
          body: snakeToCamel({
            state,
          }),
        }),
      }),
      userGet: builder.query({
        query: ({ id }: any) => ({
          url: `/users/get/${id}`,
          method: RequestMethod.GET,
        }),
        providesTags: ["user"],
        transformResponse: (response: any) => snakeToCamel(response.data),
      }),
      getUserInfo: builder.query({
        query: ({ id }) => ({
          url: `/admin/get_user/${id}`,
          method: RequestMethod.GET,
        }),
      }),

      trainingCreatedBy: builder.query({
        query: ({ branchId }: any) => ({
          url: `branchs/${branchId}/users/training-created-by`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["users"],
      }),
      nutritionCreatedBy: builder.query({
        query: ({ branchId }: any) => ({
          url: `branchs/${branchId}/users/nutrition-created-by`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["users"],
      }),
      tagMarketing: builder.query({
        query: ({ branchId }: any) => ({
          url: `branchs/${branchId}/users/tag-marketing`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["users"],
      }),
      updateSetUser: builder.mutation({
        query: ({ id, userData }) => ({
          url: `admin/users/update/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake(userData),
        }),
      }),
      firmaContrac: builder.mutation({
        query: (userData) => ({
          url: `admin/user/firm`,
          method: RequestMethod.POST,
          headers: { "Content-Type": "application/json" },
          body: camelToSnake(userData),
        }),
      }),
      updatePass: builder.mutation({
        query: ({ id, userData }) => ({
          url: `admin/user/resertPasw/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake(userData),
        }),
      }),
      webApp: builder.mutation({
        query: ({ id }) => ({
          url: `admin/user/webapp/redirect/${id}`,
          method: RequestMethod.POST,
          headers: { "Content-Type": "application/json" },
        }),
      }),
      createTag: builder.mutation({
        query: (data) => ({
          url: `admin/user/createTag`, // ajusta la ruta
          method: RequestMethod.POST,
          headers: { "Content-Type": "application/json" },
          body: camelToSnake(data),
        }),
      }),
      getEarningsMonth: builder.query({
        query: ({ userId, branchIds }) => ({
          url: `admin/user/earnings/${userId}`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: {
            branch_ids: branchIds, // se espera un array
          },
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: ["user"],
      }),
      getAverageLife: builder.query({
        query: ({ userId, branchIds }) => ({
          url: `admin/user/getAverageLife`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: {
            user: userId,
            branch_ids: branchIds,
          },
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: ["user"],
      }),
      getTransactions: builder.query({
        query: ({ userId }) => ({
          url: `admin/user/transactions/${userId}`,
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: ["user"],
      }),
      getTicket: builder.mutation({
        query: (id) => ({
          url: `admin/user/ticket`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          responseHandler: (response) => response.blob(),
          body: { id },
        }),
      }),
      getpaymentMethods: builder.query({
        query: ({ userId }) => ({
          url: `admin/users/paymentMethods/${userId}`,
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: ["user"],
      }),
      userDelete: builder.mutation({
        query: ({ id }) => ({
          url: `admin/users/delete/${id}`,
          method: RequestMethod.DELETE,
        }),
      }),
      supportStaff: builder.query({
        query: ({ id }: any) => ({
          url: `users/support-staff/${id}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["user"],
      }),
      leadTasks: builder.query({
        query: ({ id }: any) => ({
          url: `lead-tasks/${id}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["user"],
      }),
      getStatusUserDiscount: builder.query({
        query: ({ branchId, feeId, userId }) => ({
          url: `branchs/${branchId}/users/status-user-discount/${userId}`,
          method: RequestMethod.POST,
          headers: { "Content-Type": "application/json" },
          body: camelToSnake({
            feeId,
          }),
        }),
      }),

      createNewClientCaption: builder.mutation({
        query: ({ userData }) => {
          const formData = new FormData();

          return {
            url: `leads/users`,
            method: RequestMethod.POST,
            body: formData,
          };
        },
      }),
    }),
  });

export const {
  useUpdateUserMutation,
  useDeleteUserMutation,
  useFetchUsersMutation,
  useUsersQuery,
  useStoreUserMutation,
  useUserQuery,
  useGetAdvanceQuery,
  useLazyExportUsersQuery,
  useStateUserMutation,
  useUserGetQuery,
  useGetUserInfoQuery,
  useTagMarketingQuery,
  useTrainingCreatedByQuery,
  useNutritionCreatedByQuery,
  useUpdateSetUserMutation,
  useFirmaContracMutation,
  useUpdatePassMutation,
  useWebAppMutation,
  useGetEarningsMonthQuery,
  useGetAverageLifeQuery,
  useGetTransactionsQuery,
  useGetTicketMutation,
  useGetpaymentMethodsQuery,
  useUserDeleteMutation,
  useCreateTagMutation,
  useSupportStaffQuery,
  useLeadTasksQuery,
  useGetStatusUserDiscountQuery,
  useCreateNewClientCaptionMutation,
} = usersApi;
